import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-[14px] transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        solid: "",
        soft: "",
        outline: "border",
        plain: "",
        link: "underline-offset-4 hover:underline",
      },
      color: {
        primary: "",
        secondary: "",
        destructive: "",
        warning: "",
        success: "",
        neutral: "",
        dark: "",
      },
      size: {
        auto: "p-0",
        xxs: "px-1 py-0.5",
        xsm: "px-2 py-1",
        xmd: "px-3 py-2",
        xlg: "px-4 py-2.5",
        xxl: "px-5 py-3.5",
        cta: "px-8 py-4",
      },
    },
    compoundVariants: [
      // solid
      { variant: "solid", color: "primary", className: "bg-primary text-primary-foreground shadow hover:bg-primary/90" },
      { variant: "solid", color: "secondary", className: "bg-secondary text-secondary-foreground shadow hover:bg-secondary/90" },
      { variant: "solid", color: "destructive", className: "bg-destructive text-destructive-foreground hover:bg-destructive/90" },
      { variant: "solid", color: "warning", className: "bg-warning text-warning-foreground hover:bg-warning/90" },
      { variant: "solid", color: "success", className: "bg-success text-success-foreground hover:bg-success/90" },
      { variant: "solid", color: "neutral", className: "bg-dark text-dark-foreground hover:bg-foreground-dark/90" },
      {variant: "solid", color: "dark", className: "bg-dark text-dark-foreground hover:bg-foreground-dark/90" },
      // soft
      { variant: "soft", color: "primary", className: "bg-primary/15 text-primary hover:bg-primary/25" },
      { variant: "soft", color: "secondary", className: "bg-secondary/15 text-secondary hover:bg-secondary/25" },
      { variant: "soft", color: "destructive", className: "bg-destructive/15 text-destructive-accessible hover:bg-destructive/25" },
      { variant: "soft", color: "warning", className: "bg-warning/15 text-warning-accessible hover:bg-warning/25" },
      { variant: "soft", color: "success", className: "bg-success/15 text-success-accessible hover:bg-success/25" },
      { variant: "soft", color: "neutral", className: "bg-muted text-muted-foreground hover:bg-muted/80" },
      { variant: "soft", color: "dark", className: "bg-darkg/15 text-dark hover:bg-dark/25" },
      // outline
      { variant: "outline", color: "primary", className: "border-primary text-primary hover:bg-primary/10" },
      { variant: "outline", color: "secondary", className: "border-secondary text-secondary hover:bg-secondary/10" },
      { variant: "outline", color: "destructive", className: "border-destructive text-destructive-accessible hover:bg-destructive/10" },
      { variant: "outline", color: "warning", className: "border-warning text-warning-accessible hover:bg-warning/10" },
      { variant: "outline", color: "success", className: "border-success text-success-accessible hover:bg-success/10" },
      { variant: "outline", color: "neutral", className: "border-input bg-card hover:bg-surface hover:text-surface-foreground" },
      { variant: "outline", color: "dark", className: "border-dark bg-card hover:bg-surface hover:text-surface-foreground" },
      // plain
      { variant: "plain", color: "primary", className: "text-primary hover:bg-primary/10" },
      { variant: "plain", color: "secondary", className: "text-secondary hover:bg-secondary/10" },
      { variant: "plain", color: "destructive", className: "text-destructive-accessible hover:bg-destructive/10" },
      { variant: "plain", color: "warning", className: "text-warning-accessible hover:bg-warning/10" },
      { variant: "plain", color: "success", className: "text-success-accessible hover:bg-success/10" },
      { variant: "plain", color: "neutral", className: "hover:bg-surface hover:text-surface-foreground" },
      { variant: "plain", color: "dark", className: "text-dark hover:bg-dark/10" },
      // link
      { variant: "link", color: "primary", className: "text-primary hover:text-primary/80" },
      { variant: "link", color: "secondary", className: "text-secondary hover:text-secondary/80" },
      { variant: "link", color: "destructive", className: "text-destructive-accessible hover:text-destructive-accessible/80" },
      { variant: "link", color: "warning", className: "text-warning-accessible hover:text-warning-accessible/80" },
      { variant: "link", color: "success", className: "text-success-accessible hover:text-success-accessible/80" },
      { variant: "link", color: "neutral", className: "text-foreground hover:text-foreground/80" },
      { variant: "link", color: "dark", className: "text-dark-foreground hover:text-dark-foreground/80" },
    ],
    defaultVariants: {
      variant: "solid",
      color: "primary",
      size: "xmd",
    },
  }
)

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, color, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, color, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
